import { faSliders } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import SkeletonLoader from 'tiny-skeleton-loader-react';
import useCreateConsignmentAdjustment from '../../../api/consignment/useCreateConsignmentAdjustment';
import useGetAddressConsignmentStock from '../../../api/consignment/useGetAddressConsignmentStock';
import useGetAddressConsignmentMovements from '../../../api/consignment/useGetAddressConsignmentMovements';
import MainButton from '../../../components/agv-button';
import AgrivitechPagination from '../../../components/agv-pagination';
import EmptyList from '../../../components/agv-empty-list';
import {
  CONSIGNMENT_DIRECTION_LABEL,
  CONSIGNMENT_NEGATIVE_STOCK_CLASS,
  formatConsignmentMovementQuantity,
  formatConsignmentStockValue,
  formatConsignmentTourStep,
  isConsignmentAdjustment,
} from '../../../utils/consignment.util';
import AddressConsignmentAdjustmentModal from './address-consignment-adjustment-modal';

const TABLE_WRAPPER_CLASS =
  'tw-overflow-x-auto tw-rounded-lg tw-border tw-border-solid tw-border-[#dfe3e6]';

const TABLE_HEAD_CELL_CLASS =
  'tw-py-2.5 tw-px-3 tw-font-medium tw-text-[13px] tw-text-[rgba(0,0,0,0.70)] tw-bg-[#eef0f2] tw-border-solid tw-border-0 tw-border-b tw-border-[#dfe3e6]';

function TableHeadCell({ children, align = 'left' }) {
  return (
    <th
      className={`${TABLE_HEAD_CELL_CLASS} ${
        align === 'right' ? 'tw-text-right' : 'tw-text-left'
      }`}
    >
      {children}
    </th>
  );
}

function StockTable({ stocks, isLoading }) {
  if (isLoading) {
    return (
      <div className="tw-space-y-2">
        {[...Array(3)].map((_, i) => (
          <SkeletonLoader
            key={i}
            width="100%"
            height={32}
            style={{ borderRadius: 4 }}
          />
        ))}
      </div>
    );
  }

  if (!stocks?.length) {
    return (
      <div className="tw-text-[13px] tw-font-work tw-text-[rgba(0,0,0,0.55)]">
        Aucun stock consigné enregistré pour ce destinataire.
      </div>
    );
  }

  return (
    <div className={TABLE_WRAPPER_CLASS}>
      <table className="tw-w-full tw-text-[13px] tw-font-work tw-border-collapse">
        <thead>
          <tr>
            <TableHeadCell>Type</TableHeadCell>
            <TableHeadCell>Libellé</TableHeadCell>
            <TableHeadCell align="right">Solde</TableHeadCell>
          </tr>
        </thead>
        <tbody>
          {stocks.map((row) => (
            <tr
              key={row.packaging_id || row.packaging_code}
              className="tw-border-solid tw-border-0 tw-border-b tw-border-background/50 last:tw-border-b-0"
            >
              <td className="tw-py-2.5 tw-px-3 tw-text-[rgba(0,0,0,0.90)]">
                {row.packaging_code}
              </td>
              <td className="tw-py-2.5 tw-px-3 tw-text-[rgba(0,0,0,0.90)]">
                {row.packaging_label}
              </td>
              <td className="tw-py-2.5 tw-px-3 tw-text-right">
                {formatConsignmentStockValue(row.stock)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MovementRow({ movement }) {
  const isAdjustment = isConsignmentAdjustment(movement);

  return (
    <tr
      data-testid={isAdjustment ? 'consignment-movement-adjustment' : undefined}
      className={`tw-border-solid tw-border-0 tw-border-b tw-border-background/50 last:tw-border-b-0 ${
        isAdjustment ? 'tw-bg-violet-50' : ''
      }`}
    >
      <td className="tw-py-2.5 tw-px-3 tw-text-[rgba(0,0,0,0.90)] tw-whitespace-nowrap">
        {moment(movement.created_at).format('DD/MM/YYYY HH:mm')}
      </td>
      <td className="tw-py-2.5 tw-px-3">
        <span
          className={
            isAdjustment
              ? 'tw-text-violet-700 tw-font-medium'
              : movement.direction === 'LIVRAISON'
                ? 'tw-text-emerald-700'
                : 'tw-text-blue-700'
          }
        >
          {CONSIGNMENT_DIRECTION_LABEL[movement.direction] ||
            movement.direction}
        </span>
      </td>
      <td className="tw-py-2.5 tw-px-3 tw-text-[rgba(0,0,0,0.90)]">
        {movement.packaging_label}
        <span className="tw-text-[rgba(0,0,0,0.45)] tw-ml-1">
          ({movement.packaging_code})
        </span>
      </td>
      <td className="tw-py-2.5 tw-px-3 tw-text-right tw-font-bold">
        {formatConsignmentMovementQuantity(
          movement.direction,
          movement.quantity
        )}
      </td>
      <td className="tw-py-2.5 tw-px-3 tw-text-[rgba(0,0,0,0.90)]">
        {isAdjustment ? (
          <span className="tw-whitespace-pre-line tw-break-words">
            {movement.comment || '-'}
          </span>
        ) : (
          formatConsignmentTourStep(movement)
        )}
      </td>
      <td className="tw-py-2.5 tw-px-3 tw-text-[rgba(0,0,0,0.90)]">
        {isAdjustment
          ? movement.user_name ||
            (movement.user_id ? `Utilisateur #${movement.user_id}` : '-')
          : movement.driver_name ||
            (movement.driver_id ? `#${movement.driver_id}` : '-')}
      </td>
    </tr>
  );
}

function MovementsTable({ movements, isLoading }) {
  if (isLoading) {
    return (
      <div className="tw-space-y-2 tw-mt-4">
        {[...Array(4)].map((_, i) => (
          <SkeletonLoader
            key={i}
            width="100%"
            height={36}
            style={{ borderRadius: 4 }}
          />
        ))}
      </div>
    );
  }

  if (!movements?.length) {
    return (
      <div className="tw-text-[13px] tw-font-work tw-text-[rgba(0,0,0,0.55)] tw-mt-4">
        Aucun mouvement enregistré.
      </div>
    );
  }

  return (
    <div className={`${TABLE_WRAPPER_CLASS} tw-mt-2`}>
      <table className="tw-w-full tw-text-[13px] tw-font-work tw-border-collapse">
        <thead>
          <tr>
            <TableHeadCell>Date</TableHeadCell>
            <TableHeadCell>Sens</TableHeadCell>
            <TableHeadCell>Type</TableHeadCell>
            <TableHeadCell align="right">Quantité</TableHeadCell>
            <TableHeadCell>Tournée / Étape · Motif</TableHeadCell>
            <TableHeadCell>Chauffeur / Auteur</TableHeadCell>
          </tr>
        </thead>
        <tbody>
          {movements.map((movement) => (
            <MovementRow key={movement.id} movement={movement} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AddressConsignedPackagings({
  isFocused,
  addressId,
  onStockChange,
}) {
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isAdjustmentOpen, setIsAdjustmentOpen] = useState(false);
  const [movementFilters, setMovementFilters] = useState({
    limit: 10,
    offset: 0,
  });

  const {
    data: stockData,
    isLoading: isStockLoading,
    mutate: mutateStock,
  } = useGetAddressConsignmentStock(
    addressId && shouldFetch ? addressId : null
  );

  const { isLoading: isAdjustmentLoading, createConsignmentAdjustment } =
    useCreateConsignmentAdjustment();

  const {
    data: movementsData,
    isLoading: isMovementsLoading,
    error: movementsError,
    mutate: mutateMovements,
  } = useGetAddressConsignmentMovements(
    addressId && shouldFetch ? addressId : null,
    movementFilters,
    0
  );

  useEffect(() => {
    if (isFocused && addressId && !shouldFetch) {
      setShouldFetch(true);
    }
  }, [isFocused, addressId, shouldFetch]);

  useEffect(() => {
    if (!isFocused) {
      setMovementFilters({ limit: 10, offset: 0 });
    }
  }, [isFocused]);

  const totalStock = stockData?.total_stock;
  const hasNegativeStock = stockData?.stocks?.some((s) => s.stock < 0);

  const handleCreateAdjustment = async ({ packagingId, quantity, comment }) => {
    const adjustment = await createConsignmentAdjustment({
      addressId,
      packagingId,
      quantity,
      comment,
    });

    if (!adjustment) return;

    setIsAdjustmentOpen(false);
    mutateStock();
    if (movementFilters.offset === 0) {
      mutateMovements();
    } else {
      // Retour en première page : l'ajustement est le mouvement le plus récent.
      setMovementFilters((prev) => ({ ...prev, offset: 0 }));
    }
    onStockChange?.();
  };

  return (
    <div className="tw-mt-6">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
        <div className="tw-text-[16px] tw-font-work tw-font-medium tw-leading-4 tw-text-[rgba(0,0,0,0.90)]">
          Stock courant par type
        </div>
        <div className="tw-flex tw-items-center tw-gap-4">
          {totalStock !== undefined && totalStock !== null && (
            <div className="tw-text-[13px] tw-font-work tw-text-[rgba(0,0,0,0.55)]">
              Total :{' '}
              <span className="tw-text-[rgba(0,0,0,0.90)]">
                {formatConsignmentStockValue(totalStock)}
              </span>
              {hasNegativeStock && (
                <span
                  className={`tw-ml-2 tw-text-[11px] ${CONSIGNMENT_NEGATIVE_STOCK_CLASS}`}
                >
                  Anomalie à réconcilier
                </span>
              )}
            </div>
          )}
          <MainButton
            icon={faSliders}
            text={'Ajuster le stock'}
            variant={'secondary'}
            onClick={() => setIsAdjustmentOpen(true)}
            disabled={!addressId}
          />
        </div>
      </div>

      <StockTable stocks={stockData?.stocks} isLoading={isStockLoading} />

      <div className="tw-mt-8">
        <div className="tw-text-[16px] tw-font-work tw-font-medium tw-leading-4 tw-text-[rgba(0,0,0,0.90)] tw-mb-3">
          Historique des mouvements
        </div>

        {movementsError ? (
          <div className="tw-mt-4 tw-text-[13px] tw-font-work tw-text-red-600">
            {movementsError}
          </div>
        ) : !isMovementsLoading && !movementsData?.items?.length ? (
          <EmptyList />
        ) : (
          <>
            <MovementsTable
              movements={movementsData?.items}
              isLoading={isMovementsLoading}
            />
            <AgrivitechPagination
              data={movementsData}
              setOffset={(value) =>
                setMovementFilters((prev) => ({ ...prev, offset: value }))
              }
            />
          </>
        )}
      </div>

      <AddressConsignmentAdjustmentModal
        isOpen={isAdjustmentOpen}
        setIsOpen={setIsAdjustmentOpen}
        stocks={stockData?.stocks}
        createAdjustment={handleCreateAdjustment}
        mainActionIsLoading={isAdjustmentLoading}
      />
    </div>
  );
}
